import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { authFetch, AUTH_API_URL } from '../config/api';

export type CantonStatus = 'none' | 'pending' | 'provisioning' | 'done' | 'failed';

export interface User {
  id: string;
  username: string;
  email: string;
  status: string;
  role: string;
  canton_party_id: string | null;
  preapproval_cid: string | null;
  canton_status: CantonStatus;
  canton_ready: boolean;
  recovery_key_shown: boolean;
  /** La fonction de lien X existe-t-elle cote serveur ? Distinct de « ce membre l'a utilisee ». */
  x_available?: boolean;
  x_linked?: boolean;
  x_username?: string | null;
  x_avatar_id?: string | null;
  created_at: string;
}

interface AuthResult {
  error?: string;
  /** Machine-readable code from the API, e.g. NON_CUSTODIAL when the account signs in by key. */
  code?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;

  register: (username: string, email: string, inviteCode: string) => Promise<AuthResult>;
  verifyEmail: (email: string, code: string) => Promise<AuthResult>;
  login: (email: string, password: string) => Promise<AuthResult>;
  logout: () => void;
  applySession: (data: { access_token: string; refresh_token: string; user: User }) => void;
  resendVerification: (email: string) => Promise<AuthResult>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

/**
 * The last profile `/me` returned, kept with the tokens.
 *
 * ⚠️ It lets the app open on the right screen AT ONCE — the password, for a member who has a
 * wallet — instead of waiting for /me, which on a slow connection to our server was most of the
 * « ça met du temps à charger » of 06/10. /me still runs, in the background, and has the last word:
 * a session the server refuses ends here as before. It is the same data the tokens already unlock,
 * stored where they are, and it is removed with them.
 */
const USER_KEY = 'nuxaris_user';

function cachedUser(): User | null {
  try {
    if (!localStorage.getItem('access_token') && !localStorage.getItem('refresh_token')) return null;
    const raw = localStorage.getItem(USER_KEY);
    const u = raw ? JSON.parse(raw) : null;
    return u && typeof u.id === 'string' ? u : null;
  } catch {
    return null;
  }
}

/** Waits between attempts when /me could not be reached (429, 5xx, network). */
const ME_RETRY_MS = [1_000, 3_000, 8_000, 15_000];

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUserState] = useState<User | null>(cachedUser);
  // Nothing to wait for when the profile is known; it is checked behind the screen.
  const [loading, setLoading] = useState(() => !!localStorage.getItem('access_token') && !cachedUser());

  const setUser = useCallback((u: User | null) => {
    setUserState(u);
    try {
      if (u) localStorage.setItem(USER_KEY, JSON.stringify(u));
      else localStorage.removeItem(USER_KEY);
    } catch { /* storage full or blocked: the session still works, it just opens slower */ }
  }, []);

  /**
   * true: signed in (profile refreshed). false: the server ended the session. null: could not
   * tell — a 429, a 5xx, a dropped connection. ⚠️ Only `false` signs the member out: treating «
   * could not tell » as « signed out » is what put members on the email screen behind a rate limit.
   */
  const fetchMe = useCallback(async (): Promise<boolean | null> => {
    try {
      const res = await authFetch('/api/auth/me');
      if (res.ok) {
        const data = await res.json();
        setUser(data.user);
        return true;
      }
      // authFetch removes the tokens when /refresh refuses the session; /me 404 is a closed account.
      if (res.status === 404 || !localStorage.getItem('refresh_token')) {
        setUser(null);
        return false;
      }
    } catch { /* network: could not tell */ }
    return null;
  }, [setUser]);

  useEffect(() => {
    if (!localStorage.getItem('access_token')) { setLoading(false); return; }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const attempt = async (n: number) => {
      const r = await fetchMe();
      if (cancelled) return;
      if (r !== null || n >= ME_RETRY_MS.length) { setLoading(false); return; }
      timer = setTimeout(() => attempt(n + 1), ME_RETRY_MS[n]);
    };
    attempt(0);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [fetchMe]);

  const register = useCallback(async (username: string, email: string, inviteCode: string): Promise<AuthResult> => {
    try {
      const res = await fetch(`${AUTH_API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, email, invite_code: inviteCode }),
      });
      const data = await res.json();
      if (!res.ok) return { error: data.error || data.message || 'Registration failed' };
      return {};
    } catch {
      return { error: 'Network error. Please try again.' };
    }
  }, []);

  const verifyEmail = useCallback(async (email: string, code: string): Promise<AuthResult> => {
    try {
      const res = await fetch(`${AUTH_API_URL}/api/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, token: code }),
      });
      const data = await res.json();
      if (!res.ok) return { error: data.error || data.message || 'Verification failed' };

      // Verification opens a session right away: at this point the user has
      // no wallet yet, hence nothing to present on the sign-in page.
      if (data.access_token && data.refresh_token && data.user) {
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('refresh_token', data.refresh_token);
        setUser(data.user);
      }
      return {};
    } catch {
      return { error: 'Network error. Please try again.' };
    }
  }, [setUser]);

  const login = useCallback(async (email: string, password: string): Promise<AuthResult> => {
    try {
      const res = await fetch(`${AUTH_API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) return { error: data.error || data.message || 'Login failed', code: data.code };

      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);
      setUser(data.user);
      return {};
    } catch {
      return { error: 'Network error. Please try again.' };
    }
  }, [setUser]);

  // Adopts a session obtained outside the classic login (key-signature sign-in).
  const applySession = useCallback((data: { access_token: string; refresh_token: string; user: User }) => {
    localStorage.setItem('access_token', data.access_token);
    localStorage.setItem('refresh_token', data.refresh_token);
    setUser(data.user);
  }, [setUser]);

  const logout = useCallback(() => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(null);
  }, [setUser]);

  const resendVerification = useCallback(async (email: string): Promise<AuthResult> => {
    try {
      const res = await fetch(`${AUTH_API_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (res.status === 429) return { error: 'Please wait a minute before trying again.' };
      const data = await res.json();
      if (!res.ok) return { error: data.error || data.message || 'Request failed' };
      return {};
    } catch {
      return { error: 'Network error. Please try again.' };
    }
  }, []);

  const refreshUser = useCallback(async () => {
    await fetchMe();
  }, [fetchMe]);

  const value = useMemo(() => ({
    user, loading,
    register, verifyEmail, login, logout, applySession,
    resendVerification, refreshUser,
  }), [user, loading, register, verifyEmail, login, logout, applySession, resendVerification, refreshUser]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
